import { RootState } from "@store/index";
import { createSelector } from "reselect";
import { TestState, VirtualAssistanceStatus } from "@store/tests/testSlice";
import { selectScene } from "@utils/redux-utils/selectors/productSelectors";
import {
  CoverTestDataSnapshot,
  CPTConfigMode,
  CPTCustomConfiguration,
  EyeCoverAction,
  TestStatus,
  VirtualAssistantConfig,
  WCSTTrial,
} from "types/testTypes";

const testState = (state: RootState) => state.test as TestState;

export const getSelectedTestName = createSelector(
  [testState],
  (state) => state.configuration?.name,
);

export const selectTestUIConfiguration = createSelector(
  [testState],
  (state) => state.uiConfiguration,
);

export const selectCompletionPercentage = createSelector(
  [testState],
  (state) => state.completedPercentage,
);

export const selectTestConfiguration = createSelector([testState], (state) => state.configuration);

export const selectTestEventCodes = createSelector([testState], (state) => state.testEventCodes);

export const selectPersistedConfiguration = createSelector(
  [testState],
  (state) => state.persistedConfiguration,
);

export const getAvailableConfigurations = createSelector(
  [testState],
  (state) => state.availableConfigurations,
);

export const selectBaseAvailablePreTestConfigurationOptions = createSelector(
  [testState],
  (state) => state.baseAvailableConfigurations,
);

export const selectReadingAssessmentCatalog = createSelector(
  [testState],
  (state) => state.readingAssessmentCatalog,
);

export const selectAvailableEyeConfigurations = createSelector(
  [testState],
  (state) => state.availableConfigurations?.eyeConfigurationOptions ?? null,
);

export const selectTestStatus = createSelector([testState], (state) => state.status);

export const selectIsTestCompleted = createSelector(
  [testState],
  (state) => state.status === TestStatus.Completed,
);

export const selectTestId = createSelector([testState], (state) => state.testId);

export const selectCollectedTestData = createSelector(
  [testState],
  (state) => state.collectedTestData,
);

export const selectLatestAntiSaccadeSample = createSelector([testState], (state) => {
  const samples = state.collectedTestData?.antiSaccadeSamples;
  return samples?.length ? samples[samples.length - 1] : null;
});

export const selectDyscalculiaTaskResults = createSelector(
  [testState],
  (state) => state.collectedTestData?.dyscalculiaTaskResults || [],
);

export const selectReadingAssessmentComprehensionAnswers = createSelector(
  [testState],
  (state) => state.collectedTestData?.readingAssessmentComprehensionAnswers ?? null,
);

function computeWCSTAverageReactionTimeMs(trials: WCSTTrial[]): number | null {
  const withTime = trials.filter((t) => typeof t.reactionTimeMs === "number");
  if (withTime.length === 0) return null;
  const sum = withTime.reduce((acc, t) => acc + (t.reactionTimeMs ?? 0), 0);
  return Math.round(sum / withTime.length);
}

function computeWCSTAverageTrialsToFindRule(trials: WCSTTrial[]): number | null {
  if (trials.length === 0) return null;
  const counts: number[] = [];
  let segmentStart = 0;
  let currentRule = trials[0].rule;
  for (let i = 1; i <= trials.length; i++) {
    const ruleChanged = i === trials.length || trials[i]?.rule !== currentRule;
    if (ruleChanged) {
      const segment = trials.slice(segmentStart, i);
      const firstCorrectIdx = segment.findIndex((t) => t.correct);
      const trialsToFind = firstCorrectIdx >= 0 ? firstCorrectIdx + 1 : segment.length;
      counts.push(trialsToFind);
      if (i < trials.length && trials[i]) {
        segmentStart = i;
        currentRule = trials[i].rule;
      }
    }
  }
  if (counts.length === 0) return null;
  const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
  return Math.round(avg * 10) / 10;
}

export const selectWCSTData = createSelector([testState], (state) => {
  const raw = state.collectedTestData?.wcstData ?? {
    trials: [],
    averageReactionTimeMs: null,
    averageTrialsToFindRule: null,
  };
  const trials = raw.trials ?? [];
  const averageReactionTimeMs =
    computeWCSTAverageReactionTimeMs(trials) ?? raw.averageReactionTimeMs ?? null;
  const averageTrialsToFindRule =
    computeWCSTAverageTrialsToFindRule(trials) ?? raw.averageTrialsToFindRule ?? null;
  return {
    trials,
    averageReactionTimeMs,
    averageTrialsToFindRule,
  };
});

export const selectIsCPTTestProgressForAdult = createSelector(
  [testState],
  (state) =>
    (state.configuration?.extraData as CPTCustomConfiguration)?.cptConfigMode ===
    CPTConfigMode.Adult,
);

export const selectSelectedTestType = createSelector(
  [testState],
  (state) => state.configuration?.testType,
);

// Cover Test specific selectors
const selectCoverTestData = createSelector(
  [testState],
  (state) => state?.collectedTestData?.coverTestData,
);

const selectRightGazeAfterRightUncovered = createSelector(
  [selectCoverTestData],
  (coverTestData) => coverTestData?.[EyeCoverAction.OpenRightEyeLeftKeptOpen] || [],
);

const selectRightGazeAfterLeftCovered = createSelector(
  [selectCoverTestData],
  (coverTestData) => coverTestData?.[EyeCoverAction.CoverLeftEyeRightKeptOpen] || [],
);

const selectLeftGazeAfterLeftUncovered = createSelector(
  [selectCoverTestData],
  (coverTestData) => coverTestData?.[EyeCoverAction.OpenLeftEyeRightKeptOpen] || [],
);

const selectLeftGazeAfterRightCovered = createSelector(
  [selectCoverTestData],
  (coverTestData) => coverTestData?.[EyeCoverAction.CoverRightEyeLeftKeptOpen] || [],
);

export const selectLeftGazeAfterRightCoveredAnglesOrPositions = createSelector(
  [selectLeftGazeAfterRightCovered],
  (snapshots) =>
    snapshots
      ?.map((snapshot) => {
        const angles = (snapshot as CoverTestDataSnapshot)?.leftGazeAngles;
        const pos = (snapshot as CoverTestDataSnapshot)?.leftGazePosition;
        if (angles) return { value: angles, isDegree: true };
        if (pos) return { value: pos, isDegree: false };
        return null;
      })
      ?.filter(Boolean),
);

export const selectRightGazeAfterLeftCoveredAnglesOrPositions = createSelector(
  [selectRightGazeAfterLeftCovered],
  (snapshots) =>
    snapshots
      ?.map((snapshot) => {
        const angles = (snapshot as CoverTestDataSnapshot)?.rightGazeAngles;
        const pos = (snapshot as CoverTestDataSnapshot)?.rightGazePosition;
        if (angles) return { value: angles, isDegree: true };
        if (pos) return { value: pos, isDegree: false };
        return null;
      })
      ?.filter(Boolean),
);

export const selectLeftGazeAfterLeftUncoveredAnglesOrPositions = createSelector(
  [selectLeftGazeAfterLeftUncovered],
  (snapshots) =>
    snapshots
      ?.map((snapshot) => {
        const angles = (snapshot as CoverTestDataSnapshot)?.leftGazeAngles;
        const pos = (snapshot as CoverTestDataSnapshot)?.leftGazePosition;
        if (angles) return { value: angles, isDegree: true };
        if (pos) return { value: pos, isDegree: false };
        return null;
      })
      ?.filter(Boolean),
);

export const selectRightGazeAfterRightUncoveredAnglesOrPositions = createSelector(
  [selectRightGazeAfterRightUncovered],
  (snapshots) =>
    snapshots
      ?.map((snapshot) => {
        const angles = (snapshot as CoverTestDataSnapshot)?.rightGazeAngles;
        const pos = (snapshot as CoverTestDataSnapshot)?.rightGazePosition;
        if (angles) return { value: angles, isDegree: true };
        if (pos) return { value: pos, isDegree: false };
        return null;
      })
      ?.filter(Boolean),
);

export const selectCoverTestLeftDistanceData = createSelector(
  [selectCoverTestData],
  (coverTestData) => coverTestData?.leftDistanceData || [],
);
export const selectCoverTestRightDistanceData = createSelector(
  [selectCoverTestData],
  (coverTestData) => coverTestData?.rightDistanceData || [],
);
export const selectCoverTestReferenceDatas = createSelector(
  [selectCoverTestData],
  (coverTestData) => coverTestData?.referenceDatas || [],
);
export const selectContrastSensitivityLetterSet = createSelector(
  [testState],
  (state) =>
    state.collectedTestData?.contrastSensitivityOrPelliRobsonData?.pelliRobsonData
      ?.pelliRobsonLetterSet || [],
);
export const selectContrastSensitivityIndex = createSelector(
  [testState],
  (state) =>
    state.collectedTestData?.contrastSensitivityOrPelliRobsonData?.pelliRobsonData
      ?.pelliRobsonIndex || 0,
);

export const selectContrastSensitivityData = createSelector(
  [testState],
  (state) =>
    state.collectedTestData?.contrastSensitivityOrPelliRobsonData?.contrastSensitivityDatas || null,
);

export const selectContrastSensitivityFpFnInfo = createSelector(
  [testState],
  (state) => state.collectedTestData?.contrastSensitivityOrPelliRobsonData?.fpFnInfo ?? null,
);

export const selectVfaFpFnInfo = createSelector(
  [testState],
  (state) => state.collectedTestData?.vfaCollectedData?.fpFnInfo ?? null,
);

const selectVfaCollectedData = createSelector(
  [testState],
  (state) => state.collectedTestData?.vfaCollectedData,
);

export const selectVfaReliabilitySnapshot = createSelector(
  [selectVfaCollectedData],
  (vfaCollectedData) => {
    if (!vfaCollectedData) return null;
    return {
      fpFnInfo: vfaCollectedData.fpFnInfo ?? null,
      blindSpotSeen: vfaCollectedData.blindSpotSeen ?? null,
      blindSpotTotal: vfaCollectedData.blindSpotTotal ?? null,
    };
  },
);

export const selectGazeDatas = createSelector(
  [testState],
  (state) => state.collectedTestData?.gazeDatas || null,
);

const selectLatestEyeImage = createSelector(
  [testState],
  (state) => state.collectedTestData?.latestEyeImageSet || null,
);

export const selectLatestLeftEyeImage = createSelector(
  [selectLatestEyeImage],
  (imageSet) => imageSet?.leftEye || null,
);

export const selectLatestRightEyeImage = createSelector(
  [selectLatestEyeImage],
  (imageSet) => imageSet?.rightEye || null,
);

export const selectPupilSizeData = createSelector(
  [testState],
  (state) => state.collectedTestData?.pupilSizeData || null,
);

export const selectEyeConfigurationEyeType = createSelector(
  [testState],
  (state) => state.configuration?.eyeConfiguration.eyeType,
);

export const selectIsAutoPauseForGazeEnabled = createSelector(
  [testState],
  (state) => state.configuration?.eyeConfiguration.autoPauseForGaze ?? false,
);

export const selectIsAutoPauseForGazeEnabledInProgress = createSelector(
  [testState],
  (state) => state.progressConfiguration?.eyeConfiguration.autoPauseForGaze ?? false,
);

export const selectAvailablePreTestConfigurationOptions = createSelector(
  [testState],
  (state) => state.availableConfigurations,
);

export const selectAvailableProgressConfigurationOptions = createSelector(
  [testState],
  (state) => state.availableProgressConfigurations,
);

export const selectTestProgressConfiguration = createSelector(
  [testState],
  (state) => state.progressConfiguration,
);

export const selectIsGazeOffBounds = createSelector(
  [testState],
  (state) => state.isGazeOffBounds ?? false,
);

export const selectIsOperatorPaused = createSelector(
  [testState],
  (state) => state.progressConfiguration?.pauseTest === true,
);

export const selectGazeDisplacement = createSelector(
  [testState],
  (state) => state.collectedTestData?.eyeDisplacements ?? null,
);

export const selectIsCurrentlyCalibrating = createSelector(
  [testState],
  (state) => state.isCurrentlyCalibrating ?? false,
);

export const selectCalibrationLifecycle = createSelector(
  [testState],
  (state) => state.calibrationLifecycle,
);

export const selectIsCalibrationUnresponsive = createSelector(
  [testState],
  (state) => state.calibrationUnresponsive ?? false,
);

const CALIBRATION_TERMINAL_STATUSES = new Set(["successful", "ended"]);

export const selectShouldShowCalibrationOverlay = createSelector(
  [selectCalibrationLifecycle, selectScene],
  (calibrationLifecycle, scene) =>
    Boolean(calibrationLifecycle) || (scene?.toLowerCase().includes("calibration") ?? false),
);

// The headset refuses STOP_TEST while its calibration scene is active (aborting
// the native Tobii collection segfaults), but TestStateTransitionView retries
// the terminate for 25s, so the request lands once the attempt ends.
export const selectHasCalibrationFailed = createSelector(
  [testState],
  (state) => state.calibrationFailedInRun ?? false,
);

export const selectIsDeviceCalibrationActive = createSelector(
  [
    selectShouldShowCalibrationOverlay,
    selectCalibrationLifecycle,
    selectIsCalibrationUnresponsive,
    selectHasCalibrationFailed,
  ],
  (shouldShowOverlay, calibrationLifecycle, isUnresponsive, hasFailed) =>
    shouldShowOverlay &&
    !isUnresponsive &&
    !hasFailed &&
    !CALIBRATION_TERMINAL_STATUSES.has(calibrationLifecycle?.status ?? ""),
);

export const selectKineticPerimetryData = createSelector(
  [testState],
  (state) => state.collectedTestData?.kineticData ?? state.collectedTestData?.kineticData,
);

export const selectKineticPerimetryGraphStat = createSelector(
  [selectKineticPerimetryData],
  (kineticData) => kineticData?.kineticGraphStat || null,
);

export const selectShouldCheckBackendCompletion = createSelector([testState], (state) => {
  const isNearCompletion = state.completedPercentage >= 80;
  const isStartedStatus = state.status === TestStatus.Started;
  const hasTestId = !!state.testId;

  return isNearCompletion && isStartedStatus && hasTestId;
});

export const selectTestCompletionCheckData = createSelector([testState], (state) => ({
  testId: state.testId,
  status: state.status,
  completedPercentage: state.completedPercentage,
  shouldCheck:
    (state.completedPercentage >= 80 || state.status === TestStatus.Started) && !!state.testId,
}));

export const selectIsVirtualAssistanceEnabled = createSelector(
  [testState],
  (state) => state.configuration?.virtualAssistanceConfig?.showAssistant || false,
);

export const selectCurrentVirtualAssistanceVideo = createSelector(
  [testState],
  (state) => state.currentVirtualAssistanceVideo,
);

export const selectVirtualAssistanceStatus = createSelector(
  [testState],
  (state): VirtualAssistanceStatus | null => state.virtualAssistanceStatus,
);

export const selectIsVirtualAssistanceRunning = createSelector(
  [selectVirtualAssistanceStatus],
  (status) => status?.state != null && status?.state !== "AllVideosCompleted",
);

export const selectVirtualAssistantConfig = createSelector(
  [testState],
  (state): VirtualAssistantConfig | null => state.configuration?.virtualAssistanceConfig || null,
);

export const selectTumblingECollectedSnapshots = createSelector(
  [testState],
  (state) => state.collectedTestData?.visualAcuitySnapshots?.tumblingESnapshots || [],
);

export const selectTumblingECurrentlyActive = createSelector(
  [testState],
  (state) => state.collectedTestData?.visualAcuitySnapshots?.currentlyActiveTumblingE || null,
);

export const selectTumblingEThresholdRow = createSelector(
  [testState],
  (state) => state.collectedTestData?.visualAcuitySnapshots?.thresholdRow ?? null,
);

export const selectTumblingEThresholdSnellenSize = createSelector(
  [testState],
  (state) => state.collectedTestData?.visualAcuitySnapshots?.thresholdSnellenSize ?? null,
);

export const selectTumblingEChartStimuli = createSelector(
  [testState],
  (state) => state.collectedTestData?.visualAcuitySnapshots?.chartStimuli ?? null,
);

export const selectVirtualAssistantDoNotShowAgainList = createSelector(
  [testState],
  (state) => state.virtualAssistanceStatus?.doNotShowAgainList || [],
);

export const selectSavedTestConfiguration = createSelector(
  [testState],
  (state) => state.savedConfiguration,
);

export const selectIsSceneReady = createSelector([testState], (state) => state.isSceneReady);

export const selectTestPhase = createSelector([testState], (state) => state.testPhase ?? null);

export const selectHeadsetActiveTest = createSelector(
  [testState],
  (state) => state.headsetActiveTest ?? null,
);

export const selectTestStartDate = createSelector(
  [testState],
  (state) => state.testStartDate ?? null,
);

export const selectTimeScale = createSelector([testState], (state) => state.timeScale);

export const selectColorVisionData = createSelector(
  [testState],
  (state) => state.collectedTestData?.colorVisionData,
);

export const selectColorVisionInitData = createSelector(
  [selectColorVisionData],
  (data) => data?.initData,
);

export const selectColorVisionLatestSnapshot = createSelector(
  [selectColorVisionData],
  (data) => data?.latestSnapshot,
);

export const selectShouldWaitForNextEyeOnFinish = createSelector(
  [testState],
  (state) => state.shouldWaitForNextEyeOnFinish,
);

export const selectIsLastOfBackToBackEyeTest = createSelector(
  [testState],
  (state) => state.isLastOfBackToBackEyeTest,
);

export const selectIsResumedTestSession = createSelector(
  [testState],
  (state) => state.isResumedTestSession ?? false,
);

export const selectIsBackToBackEyeTest = createSelector(
  [testState],
  (state) => state.configuration?.eyeConfiguration.isBackToBackEyeTest ?? false,
);

export const selectIsManualCompletionCheckInProgress = createSelector(
  [testState],
  (state) => state.isManualCompletionCheckInProgress,
);

export const selectIsManualCompletionCheckFailed = createSelector(
  [testState],
  (state) => state.isManualCompletionCheckFailed,
);

export const selectIsManualCompletionCheckSucceeded = createSelector(
  [testState],
  (state) => state.isManualCompletionCheckSucceeded,
);
